// Talks to /api/v1/admin/config-store* — the generic Group → Collection → Key JSON document store
// (appDataStore-backed). Permission-gated server-side on Admin.ConfigStore. The caller supplies the API
// bearer + identity hints. Optimistic concurrency uses the document ETag echoed as If-Match on save.
import { authHeaders, type AuthArgs } from './authArgs';

export interface ConfigKeyNode {
  name: string;
  hasBase: boolean;
  environments: string[];
}

export interface ConfigCollectionNode {
  name: string;
  keys: ConfigKeyNode[];
}

export interface ConfigGroup {
  name: string;
  collections: ConfigCollectionNode[];
}

export interface ConfigTree {
  groups: ConfigGroup[];
}

export interface ConfigDocument {
  group: string;
  collection: string;
  key: string;
  environment?: string | null;
  json: string;
  etag?: string | null;
}

/** Raised when a save or model fetch loses the optimistic-concurrency check (HTTP 409). */
export class ConfigConcurrencyError extends Error {
  constructor(message = 'This document was changed by someone else. Reload and reapply your edits.') {
    super(message);
    this.name = 'ConfigConcurrencyError';
  }
}

const base = '/api/v1/admin/config-store';

function docPath(group: string, collection: string, key: string, environment?: string | null): string {
  const path = `${base}/documents/${encodeURIComponent(group)}/${encodeURIComponent(collection)}/${encodeURIComponent(key)}`;
  return environment ? `${path}?environment=${encodeURIComponent(environment)}` : path;
}

// Normalize the document ETag from whatever casing the server serializes it as. The API returns the C# `ETag`
// property, which the default camelCase policy emits as `eTag`; older/other serializers may use `etag` or
// `ETag`. Reading only one casing silently drops the ETag, so the save omits If-Match and the backend treats
// every update as a create — 409 against the existing document. Accept any casing to stay resilient.
function normalizeConfigDocument(raw: unknown): ConfigDocument {
  const doc = raw as ConfigDocument & { eTag?: string | null; ETag?: string | null };
  const etag = doc.etag ?? doc.eTag ?? doc.ETag ?? null;
  return { ...doc, etag };
}

export async function getConfigTree(auth: AuthArgs): Promise<ConfigTree> {
  const res = await fetch(`${base}/tree`, { method: 'GET', headers: authHeaders(auth) });
  if (!res.ok) throw new Error(`config tree ${res.status}: ${await res.text()}`);
  return (await res.json()) as ConfigTree;
}

/** Selectable override environments for the "Add override" picker (from the dbo.KeyValueStore setting). */
export async function getOverrideEnvironments(auth: AuthArgs): Promise<string[]> {
  const res = await fetch(`${base}/override-environments`, { method: 'GET', headers: authHeaders(auth) });
  if (!res.ok) throw new Error(`override environments ${res.status}: ${await res.text()}`);
  return (await res.json()) as string[];
}

export async function getConfigDocument(
  auth: AuthArgs,
  group: string,
  collection: string,
  key: string,
  environment?: string | null,
): Promise<ConfigDocument | null> {
  const res = await fetch(docPath(group, collection, key, environment), { method: 'GET', headers: authHeaders(auth) });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`config document ${res.status}: ${await res.text()}`);
  return normalizeConfigDocument(await res.json());
}

export async function saveConfigDocument(
  auth: AuthArgs,
  document: { group: string; collection: string; key: string; json: string; etag?: string | null; environment?: string | null },
): Promise<ConfigDocument> {
  const headers = authHeaders(auth);
  // Echo the ETag we read the document at as If-Match (optimistic concurrency). Absent => create-only.
  if (document.etag) {
    headers['If-Match'] = document.etag;
  }
  const res = await fetch(docPath(document.group, document.collection, document.key, document.environment), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ json: document.json }),
  });
  if (res.status === 409) throw new ConfigConcurrencyError();
  if (!res.ok) throw new Error(`save config document ${res.status}: ${await res.text()}`);
  return normalizeConfigDocument(await res.json());
}

export async function deleteConfigDocument(
  auth: AuthArgs,
  group: string,
  collection: string,
  key: string,
  environment?: string | null,
): Promise<void> {
  const res = await fetch(docPath(group, collection, key, environment), { method: 'DELETE', headers: authHeaders(auth) });
  if (!res.ok && res.status !== 204) throw new Error(`delete config document ${res.status}: ${await res.text()}`);
}

export interface RoutingModelFetchRequest {
  environment: string;
  baseETag: string | null;
  environmentETag: string | null;
}

export interface RoutingModelDeployment {
  id: string;
  modelName: string;
  modelVersion: string;
  modelFormat?: string;
  provisioningState?: string;
  routingStatus?: string;
  deployment: string;
  resourceId: string;
  accountName: string;
  region: string;
  sku: string;
  quotaTPM: number;
  priority: number;
}

export interface RoutingModelChangeSummary {
  comparisonAvailable: boolean;
  added: RoutingModelDeployment[];
  removed: RoutingModelDeployment[];
  updated: {
    before: RoutingModelDeployment;
    after: RoutingModelDeployment;
    changedFields: string[];
  }[];
  unchangedCount: number;
}

export interface RoutingModelFetchResult {
  environment: string;
  fetchedAtUtc: string;
  endpointCount: number;
  deploymentCount?: number;
  inventoryComplete?: boolean;
  seedETag: string;
  applied?: boolean;
  deployments?: RoutingModelDeployment[];
  changes?: RoutingModelChangeSummary | null;
}

export interface RoutingModelSeedStatus {
  environment: string;
  effectiveTpmFailoverThresholdPercent?: number;
  fetchedAtUtc: string | null;
  endpointCount: number;
  deploymentCount?: number;
  inventoryComplete?: boolean;
  seedETag: string | null;
  available: boolean;
  needsFetch: boolean;
  deployments?: RoutingModelDeployment[];
}

async function routingModelError(res: Response): Promise<Error> {
  const guidance: Record<number, string> = {
    400: 'Model discovery is disabled or its saved configuration is invalid. Check the saved Base and environment settings.',
    401: 'Sign in again to manage routing models.',
    403: 'Model discovery is not permitted. Check your Configuration Store permission and select this API’s running environment.',
    409: 'The saved configuration or routing seed changed. Reopen this document to reload both saved layers, review the resources, and try again. Preserve any unsaved edits first.',
    502: 'Azure model discovery failed. The last known routing seed is preserved. Check the configured accounts and the API identity’s Azure read permissions, then retry.',
    503: 'The configuration store or model discovery service is unavailable. Check that the store is enabled and reachable, then check seed status again.',
  };
  let detail = '';
  try {
    const body: unknown = await res.json();
    if (typeof body === 'object' && body !== null && 'message' in body && typeof body.message === 'string') {
      detail = body.message;
    }
  } catch {
    // Do not display proxy HTML or other non-contract error bodies.
  }
  const message = `${guidance[res.status] ?? `Routing models request failed (HTTP ${res.status}). Check seed status before retrying.`}${detail ? ` ${detail}` : ''}`;
  return res.status === 409 ? new ConfigConcurrencyError(message) : new Error(message);
}

export async function fetchRoutingModels(
  auth: AuthArgs,
  request: RoutingModelFetchRequest,
): Promise<RoutingModelFetchResult> {
  const res = await fetch('/api/v1/admin/routing/models/fetch', {
    method: 'POST',
    headers: authHeaders(auth),
    body: JSON.stringify({
      environment: request.environment,
      baseETag: request.baseETag,
      environmentETag: request.environmentETag,
    }),
  });
  if (!res.ok) throw await routingModelError(res);
  return (await res.json()) as RoutingModelFetchResult;
}

/** Reads the persisted seed only; this request never queries Azure ARM. */
export async function getRoutingModelSeedStatus(auth: AuthArgs, environment: string): Promise<RoutingModelSeedStatus> {
  const res = await fetch(`/api/v1/admin/routing/models/seed?environment=${encodeURIComponent(environment)}`, {
    method: 'GET',
    headers: authHeaders(auth),
  });
  if (!res.ok) throw await routingModelError(res);
  return (await res.json()) as RoutingModelSeedStatus;
}

export type ConfigStoreBuildState =
  | 'Current'
  | 'StoreFromNewerBuild'
  | 'StoreFromOlderBuild'
  | 'PinnedByRollback'
  | 'Unknown';

export interface ConfigStoreLayerStatus {
  layer: string;
  storePresent: boolean;
  shippedPresent: boolean;
  differs: boolean;
}

export interface ConfigStoreBindingStatus {
  binding: string;
  state: ConfigStoreBuildState;
  markerVersion?: string | null;
  markerSequence?: number | null;
  rolledBackFromSequence?: number | null;
  canAccept: boolean;
  layers: ConfigStoreLayerStatus[];
}

export interface ConfigStoreBuildStatus {
  runningVersion: string;
  runningSequence?: number | null;
  canAccept: boolean;
  bindings: ConfigStoreBindingStatus[];
}

export interface ConfigStoreAcceptResult {
  binding: string;
  accepted: boolean;
  reason?: string | null;
  backedUp: string[];
}

/** How the store's deploy markers relate to the build serving this request (e.g. an older build swapped back). */
export async function getConfigDeployStatus(auth: AuthArgs): Promise<ConfigStoreBuildStatus> {
  const res = await fetch(`${base}/deploy-status`, { method: 'GET', headers: authHeaders(auth) });
  if (!res.ok) throw new Error(`config deploy status ${res.status}: ${await res.text()}`);
  return (await res.json()) as ConfigStoreBuildStatus;
}

/** Makes the running build's shipped files authoritative over a store seeded by a newer build (backed up first). */
export async function acceptRunningBuildConfig(auth: AuthArgs, bindings?: string[]): Promise<ConfigStoreAcceptResult[]> {
  const res = await fetch(`${base}/accept-running-build`, {
    method: 'POST',
    headers: { ...authHeaders(auth), 'Content-Type': 'application/json' },
    body: JSON.stringify({ bindings: bindings ?? null }),
  });
  if (!res.ok) throw new Error(`accept running build ${res.status}: ${await res.text()}`);
  return (await res.json()) as ConfigStoreAcceptResult[];
}